import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {UserProfileUpdateRequest, UserProfileUpdateResponse} from "../payload-types/user-profile";
import {WebError} from "../web-error";

/**
 * #19351: PUT {@link RevaApi.PATH_USER_PROFILE} — updates full name / e-mail / language of the logged-in
 * user. Omitted fields are left unchanged; a changed e-mail is only accepted together with
 * `currentPassword` (422 otherwise).
 *
 * 422/503 are rethrown as the raw {@link WebError} instead of being converted to a generic `ErrorI18n`
 * (unlike the base class default), so the dialog can show account-management's own `error`/`error_de`
 * message resp. the fixed "not reachable" text.
 */
export class UserProfileUpdateApiInterface extends SimpleApiInterface<UserProfileUpdateResponse> {
    constructor(private readonly payload: UserProfileUpdateRequest) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_USER_PROFILE]);
    }

    protected buildPayload(): UserProfileUpdateRequest {
        return this.payload;
    }

    protected executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<UserProfileUpdateResponse> {
        return executor.fetchPUT(url, credentials, this.buildPayload());
    }

    protected override handleApiErrors(e: Error): UserProfileUpdateResponse {
        if (e instanceof WebError && (e.status === 422 || e.status === 503)) {
            throw e;
        }
        return super.handleApiErrors(e);
    }
}
